/**
 * Once-per-login security toast (Angular LayoutComponent.checkSecurityNotification).
 *
 * Isolated from React Strict Mode remounts: the API check runs once while the pending
 * flag is set; a completed "show" result can rehydrate the banner after remount.
 */

import {
  clearSecurityNotificationPending,
  isSecurityNotificationPending,
} from '@/lib/auth/securityNotificationFlag'
import { fetchSecurityNotificationContent } from './securityNotificationService'

export type SecurityCheckResult =
  | { kind: 'hidden' }
  | { kind: 'show'; content: string; endsAt: number }

let generation = 0
let inFlight: Promise<SecurityCheckResult> | null = null
let lastResult: SecurityCheckResult | null = null

/** Call when arming a new login so a previous session's result is not reused. */
export function resetSecurityCheckSession(): void {
  generation += 1
  inFlight = null
  lastResult = null
}

/**
 * User dismissed the toast (×). Mark checked result as hidden so remounts /
 * effect re-runs do not re-show for the rest of this session.
 */
export function dismissSecurityNotificationResult(): void {
  lastResult = { kind: 'hidden' }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

/**
 * Runs the config fetch at most once per armed pending flag.
 * Clears the pending flag when the check is initiated (Angular parity).
 */
export function ensureSecurityNotificationCheck(): Promise<SecurityCheckResult> {
  if (lastResult) return Promise.resolve(lastResult)
  if (inFlight) return inFlight

  const gen = generation

  inFlight = (async (): Promise<SecurityCheckResult> => {
    // Angular layout waits ~1s before calling the config API.
    await sleep(1000)

    // Superseded by a new login / role-entry reset.
    if (gen !== generation) {
      return { kind: 'hidden' }
    }

    try {
      if (!isSecurityNotificationPending()) {
        if (lastResult) return lastResult
        return { kind: 'hidden' }
      }

      // Angular clears the flag when the check is initiated (not after the response).
      clearSecurityNotificationPending()

      const data = await fetchSecurityNotificationContent()

      if (gen !== generation) {
        return { kind: 'hidden' }
      }

      const show = data.NOTIFICATION_SHOW === 'ON'
      const content = (data.NOTIFICATION_CONTENT ?? '').trim()
      const seconds = Number(data.NOTIFICATION_TIME)

      if (!show || !content || !Number.isFinite(seconds) || seconds <= 0) {
        const hidden: SecurityCheckResult = { kind: 'hidden' }
        lastResult = hidden
        return hidden
      }

      const shown: SecurityCheckResult = {
        kind: 'show',
        content,
        endsAt: Date.now() + Math.ceil(seconds) * 1000,
      }
      lastResult = shown
      return shown
    } catch {
      if (gen !== generation) {
        return { kind: 'hidden' }
      }
      const hidden: SecurityCheckResult = { kind: 'hidden' }
      lastResult = hidden
      return hidden
    } finally {
      if (gen === generation) {
        inFlight = null
      }
    }
  })()

  return inFlight
}

export function getSecurityCheckLastResult(): SecurityCheckResult | null {
  return lastResult
}
